<template>

  <div class="vat-bill-group">

    <div
      class="bill-group-header"
      :class="{ 'is-expanded': isExpanded }"
      @click="toggle"
    >

      <div class="bill-header-left">
         <i class="fas fa-file-invoice bill-header-icon mr-2"></i> <span class="bill-title"
          >Hóa đơn #{{ resolvedBillNumber }}</span
        > <b-badge
          v-if="isErrorBill"
          variant="danger"
          class="bill-status-badge ml-2"
          > Lỗi preview </b-badge
        > <b-badge
          v-else
          variant="light"
          class="bill-count-badge ml-2"
          >{{ bill.items.length }} SP</b-badge
        >
      </div>

      <div class="bill-header-right">
         <span
          v-if="isErrorBill"
          class="bill-error-code"
          >{{ resolvedErrorCode }}</span
        > <span
          v-else
          class="bill-total"
          >{{ formatCurrency(bill.summary.totalAmount) }} VND</span
        > <i
          :class="isExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
          class="ml-2 toggle-icon"
        ></i
        >
      </div>

    </div>
     <b-collapse
      :id="collapseId"
      v-model="isExpanded"
      >
      <div class="bill-group-body">

        <div
          v-if="isErrorBill"
          class="bill-error-panel"
        >

          <div class="bill-error-title">
             <i class="fas fa-exclamation-circle mr-2"></i> {{ resolvedErrorHeadline }}
          </div>

          <div class="bill-error-message">{{ resolvedErrorMessage }}</div>

        </div>
         <VatPreviewTable
          v-else
          :items="bill.items"
          :summary="bill.summary"
        />
      </div>
       </b-collapse
    >
  </div>

</template>

<script>
  import VatPreviewTable from './VatPreviewTable.vue'

  export default {
    name: 'VatPreviewBillGroup',

    components: {
      VatPreviewTable,
    },

    props: {
      bill: {
        type: Object,
        required: true,
      },
      defaultExpanded: {
        type: Boolean,
        default: false,
      },
    },

    data() {
      return {
        isExpanded: this.defaultExpanded,
      }
    },

    computed: {
      collapseId() {
        return `collapse-bill-${this.bill.billId}`
      },

      resolvedBillNumber() {
        return this.bill.billNumber || String(this.bill.billId || '').slice(0, 8)
      },

      isErrorBill() {
        return (this.bill?.status || '').toString().trim().toLowerCase() === 'error'
      },

      resolvedErrorCode() {
        return (this.bill?.errorCode || this.bill?.reasonCode || 'UNKNOWN_ERROR')
          .toString()
          .trim()
          .toUpperCase()
      },

      resolvedErrorHeadline() {
        const byCode = {
          SOURCE_ITEMS_CONTRACT_INVALID: 'Nguồn dữ liệu hóa đơn không đúng contract',
          SOURCE_ITEM_VAT_MISSING: 'Thiếu VAT ở item nguồn để dựng preview',
          SOURCE_ITEM_VAT_INVALID: 'VAT của hàng hóa nguồn không hợp lệ, cần kiểm tra Product.VAT',
          UNSUPPORTED_VAT_RATE:
            'Cấu hình VAT không hợp lệ, cần kiểm tra cấu hình E-Invoice (discount VAT)',
          DISCOUNT_BUCKET_ALLOCATION_FAILED: 'Không phân bổ được chiết khấu theo bucket thuế',
          BILL_TYPE_MISMATCH: 'Hóa đơn không thuộc lane e-invoice đã chọn',
          WHOLESALE_INVOICE_NOT_FOUND: 'Không tìm thấy hóa đơn bán buôn',
        }
        return byCode[this.resolvedErrorCode] || 'Không thể dựng preview VAT cho hóa đơn này'
      },

      resolvedErrorMessage() {
        return this.bill?.error || 'Vui lòng kiểm tra lại dữ liệu hóa đơn nguồn trước khi thử lại.'
      },
    },

    methods: {
      toggle() {
        this.isExpanded = !this.isExpanded
      },

      formatCurrency(value) {
        if (value == null) return '0'
        return new Intl.NumberFormat('vi-VN').format(Number(value) || 0)
      },
    },
  }
</script>

<style lang="scss" scoped>
  $draft-primary: #3699ff;
  $draft-bg: #f3f6f9;
  $draft-border: #ebedf3;
  $draft-text: #3f4254;
  $draft-text-muted: #6c757d;

  .vat-bill-group {
    border: 1px solid #b7b7b7;
    border-radius: 0.375rem;
    margin-bottom: 0.5rem;
    overflow: hidden;
    background: #fff;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .bill-group-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    background: #f3f5f8;
    cursor: pointer;
    user-select: none;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover {
      background: #eef2f7;
    }

    &.is-expanded {
      border-bottom: 1px solid darken($draft-border, 4%);
    }
  }

  .bill-header-left {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .bill-header-icon {
    color: rgba($draft-primary, 0.72);
    font-size: 0.95rem;
  }

  .bill-title {
    font-weight: 700;
    color: $draft-text;
    letter-spacing: -0.01em;
    white-space: nowrap;
  }

  .bill-count-badge {
    background: rgba($draft-text-muted, 0.12);
    color: $draft-text-muted;
    font-weight: 600;
    font-size: 0.68rem;
    padding: 0.28rem 0.45rem;
  }

  .bill-status-badge {
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    padding: 0.25rem 0.45rem;
  }

  .bill-header-right {
    display: flex;
    align-items: center;
    margin-left: 1rem;
    flex-shrink: 0;
  }

  .bill-total {
    font-weight: 600;
    color: $draft-text;
    font-variant-numeric: tabular-nums;
  }

  .bill-error-code {
    font-size: 0.72rem;
    font-weight: 700;
    color: #b42318;
    letter-spacing: 0.02em;
  }

  .toggle-icon {
    color: $draft-text-muted;
    font-size: 0.92rem;
  }

  .bill-group-body {
    padding: 1rem;
    background: #fff;
  }

  .bill-error-panel {
    border: 1px solid #fecaca;
    border-radius: 0.35rem;
    background: #fff5f5;
    padding: 0.75rem 0.85rem;
  }

  .bill-error-title {
    font-weight: 700;
    color: #b42318;
    font-size: 0.8rem;
    display: flex;
    align-items: center;
  }

  .bill-error-message {
    margin-top: 0.35rem;
    color: #7a271a;
    font-size: 0.77rem;
    line-height: 1.45;
  }
</style>
